import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import DynamicField from "./components/DynamicFields";
import {
  fetchPublicForm,
  submitPublicForm,
  fetchLocationData,
} from "./services/formApi";
import { validateAllFields } from "./utils/validation";
import { getFormDataWithLabelAsField } from "./utils/helpers";
import "./styles/PublicLeadFrom.css";
import ImageWithBasePath from "../../../../core/common/imageWithBasePath";
import { notify } from "../../../../services/toast.config";

interface FormField {
  id: number;
  label: string;
  type: string;
  required: boolean;
  typeCheck: string;
  options?: string[];
}

interface FormResponse {
  public_id: string;
  title: string;
  form_name: string;
  structure: FormField[];
  access_token: string;
  expires_in: number;
  company_name: string | any;
  company_logo: string | any;
}

interface LocationData {
  countries: any[];
  states: any[];
  cities: any[];
  districts: any[];
}

const PublicLeadForm: React.FC = () => {
  const { public_id } = useParams<{ public_id: string }>();

  // ========================================================================
  // STATE MANAGEMENT
  // ========================================================================
  const [formResponse, setFormResponse] = useState<FormResponse | null>(null);
  const [fields, setFields] = useState<FormField[]>([]);
  const [formData, setFormData] = useState<{ [key: string]: any }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [status, setStatus] = useState<
    "loading" | "ready" | "submitting" | "success" | "error" | "expired"
  >("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Location data state
  const [locationData, setLocationData] = useState<LocationData>({
    countries: [],
    states: [],
    cities: [],
    districts: [],
  });

  // ========================================================================
  // LOAD FORM ON MOUNT
  // ========================================================================
  useEffect(() => {
    if (public_id) {
      loadForm();
    } else {
      setStatus("error");
      setErrorMessage("Invalid form link");
    }
  }, [public_id]);

  // ========================================================================
  // FETCH FORM STRUCTURE & TOKEN
  // ========================================================================
  const loadForm = async () => {
    setStatus("loading");
    setErrorMessage("");

    try {
      if (!public_id) {
        throw new Error("Public form ID is missing");
      }

      const response = await fetchPublicForm(public_id);

      if (response?.form_data?.expired) {
        setStatus("expired");
        return;
      }

      setFormResponse(response?.form_data);
      setFields(response?.form_data.structure || []);
      setStatus("ready");

      setLocationData(response?.locations_data);

      // Store access token in sessionStorage
      sessionStorage.setItem(
        `form_token_${public_id}`,
        response?.form_data?.access_token
      );
    } catch (error: any) {
      notify.error(error?.message || "Failed to submit the form!");
      if (error.message?.includes("not found")) {
        setStatus("error");
        setErrorMessage("Form not found or inactive");
      } else if (error.message?.includes("expired")) {
        setStatus("expired");
      } else if (error.message?.includes("Invalid origin")) {
        setStatus("error");
        setErrorMessage(
          "Access denied. This form can only be accessed from authorized domains."
        );
      } else {
        setStatus("error");
        setErrorMessage(
          error.message || "Failed to load form. Please try again."
        );
      }

      setLocationData({
        countries: [],
        states: [],
        cities: [],
        districts: [],
      });
    }
  };

  // ========================================================================
  // HANDLE FIELD CHANGES
  // ========================================================================
  const handleFieldChange = (
    fieldId: number,
    value: any,
    errorMsg?: string
  ) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));

    if (errorMsg !== undefined) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (errorMsg) {
          newErrors[fieldId] = errorMsg;
        } else {
          delete newErrors[fieldId];
        }
        return newErrors;
      });
    }
  };

  // ========================================================================
  // HANDLE FORM SUBMISSION
  // ========================================================================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    const validationErrors = validateAllFields(fields, formData);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error
      const firstErrorField = document.querySelector(".error");
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setStatus("submitting");
    setErrorMessage("");

    try {
      if (!public_id) {
        throw new Error("Public form ID is missing");
      }

      const accessToken = sessionStorage.getItem(`form_token_${public_id}`);

      if (!accessToken) {
        throw new Error("Session expired. Please refresh the page.");
      }

      const payload = getFormDataWithLabelAsField(
        fields,
        formData,
        locationData
      );
      await submitPublicForm(public_id, accessToken, payload);

      // Clear token and form data after successful submission
      sessionStorage.removeItem(`form_token_${public_id}`);

      setStatus("success");
    } catch (error: any) {
      setStatus("ready");
      notify.error(error?.message || "Failed to submit the form!");

      if (error.message?.includes("Token has already been used")) {
        setErrorMessage(
          "This form has already been submitted. Please refresh the page to submit again."
        );
      } else if (error.message?.includes("expired")) {
        setErrorMessage("Your session has expired. Please refresh the page.");
      } else if (error.message?.includes("validation failed")) {
        setErrorMessage("Form validation failed. Please check your entries.");
        if (error.errors) {
          const backendErrors: { [key: string]: string } = {};
          error.errors.forEach((err: any) => {
            backendErrors[err.field] = err.error;
          });
          setErrors(backendErrors);
        }
      } else if (error.message?.includes("Invalid origin")) {
        setErrorMessage(
          "Access denied. This form can only be submitted from authorized domains."
        );
      } else {
        setErrorMessage(
          error.message || "Failed to submit form. Please try again."
        );
      }
    } finally {
    }
  };

  // ========================================================================
  // HANDLE RESET FOR NEW SUBMISSION
  // ========================================================================
  const handleSubmitAnother = () => {
    // Clear form data and errors
    setFormData({});
    setErrors({});
    setErrorMessage("");

    // Reset status and reload form to get new token
    setStatus("loading");
    loadForm();
  };

  // ========================================================================
  // RENDER: LOADING STATE
  // ========================================================================
  if (status === "loading") {
    return (
      <div className="public-page-wrapper">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading form...</p>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: SUCCESS STATE
  // ========================================================================
  if (status === "success") {
    return (
      <div className="public-page-wrapper">
        <div className="success-state">
          <div className="success-icon">✓</div>
          <h2>Thank You!</h2>
          <p>Your response has been recorded successfully.</p>
          <p className="text-muted" style={{ marginTop: "8px" }}>
            We have received your submission and will get back to you soon.
          </p>

          {/* Submit Another Response Button */}
          <div style={{ marginTop: "24px" }}>
            <button
              className="submit-another-button"
              onClick={handleSubmitAnother}
              style={{
                padding: "12px 24px",
                backgroundColor: "#1D71E3",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                transition: "background-color 0.3s ease",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = "#155ab8";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#1D71E3";
              }}>
              <i className="ti ti-plus"></i>
              Submit Another Response
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: ERROR STATE
  // ========================================================================
  if (status === "error") {
    return (
      <div className="public-page-wrapper">
        <div className="error-state">
          <div className="error-icon">
            <i className="ti ti-alert-circle"></i>
          </div>
          <h2>Unable to Load Form</h2>
          <p className="error-message">{errorMessage}</p>
          <button className="retry-button" onClick={loadForm}>
            <i className="ti ti-refresh"></i>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: EXPIRED STATE
  // ========================================================================
  if (status === "expired") {
    return (
      <div className="public-page-wrapper">
        <div className="expired-state">
          <div className="expired-icon">
            <i className="ti ti-clock-off"></i>
          </div>
          <h2>Form No Longer Available</h2>
          <p>
            This form link has expired or has been deactivated and is no longer
            accepting responses.
          </p>
          <p className="text-muted" style={{ marginTop: "8px" }}>
            Please contact the form administrator for a new link.
          </p>
        </div>
      </div>
    );
  }

  // ========================================================================
  // RENDER: FORM
  // ========================================================================
  return (
    <div className="public-page-wrapper">
      {/* <header className="company-header">
        <div className="header-content">
          <div className="logo-section">
            <div className="company-logo">
              <li className="avatar avatar-lg avatar-rounded object-cover flex-shrink-0">
                <ImageWithBasePath
                  src={
                    !formResponse?.company_logo ||
                    formResponse?.company_logo === "null"
                      ? "assets/img/company/company-09.svg"
                      : formResponse?.company_logo
                  }
                  alt="Company Logo"
                />
              </li>
            </div>
            <div className="company-info">
              <h1 className="company-name">
                {formResponse?.company_name || "Company Name"}
              </h1>
              <p className="company-tagline">
                <p className="text-muted">We value your time and information</p>
              </p>
            </div>
          </div>
        </div>
      </header> */}

      <main className="form-main">
        <div className="form-title-section">
          <h2>{formResponse?.title || formResponse?.form_name}</h2>
          <p>
            Please fill out the form below. Fields marked with{" "}
            <span style={{ color: "#dc3545" }}>*</span> are required. All
            information will be kept confidential.
          </p>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="alert-danger">
            <div className="alert-content">
              <i className="ti ti-alert-circle"></i>
              <div className="alert-text">
                <strong>Error:</strong> {errorMessage}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="lead-form">
          <div className="form-grid">
            {fields.map((field) => (
              <DynamicField
                key={field.id}
                field={field}
                value={formData[field.id] || ""}
                error={errors[field.id]}
                onChange={handleFieldChange}
                locationData={locationData}
              />
            ))}
          </div>

          <div className="submit-section">
            <button type="submit" disabled={status === "submitting"}>
              {status === "submitting" ? (
                <>
                  <span className="button-spinner"></span>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ti ti-send" style={{ marginRight: "8px" }}></i>
                  Submit Form
                </>
              )}
            </button>
          </div>

          <div className="security-notice">
            <i className="ti ti-lock"></i>
            Your information is secure and will be handled confidentially
          </div>
        </form>
      </main>
    </div>
  );
};

export default PublicLeadForm;
